import chairSvg from '../assets/chair.svg?raw';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, pushSegment, transformUnitSegments } from '../glyphs/frame';
import { beginGlyphInstance, endGlyphInstance } from '../glyphs/glyphInstances';
import { getUnitGlyph } from '../glyphs/svgGlyphs';
import type { ParamValues } from '../paramSchema';
import type { Pt2 } from '../../utils/FloorMesh3DBuilder';
import { t } from '../../translation/markers';

const PARAMS = {
  shape: {
    kind: 'select',
    label: t('shape'),
    default: 'rectangular',
    options: [
      { value: 'rectangular', label: t('rectangular') },
      { value: 'round', label: t('round') },
    ],
  },
  chairs: { kind: 'boolean', label: t('chairs'), default: true },
  headChairs: { kind: 'boolean', label: t('head chairs'), default: false },
} as const;

/** Segments approximating the round-table ellipse. */
const ROUND_SEGMENTS = 24;

/** Footprint of one chair symbol around the table (matches the Chair type). */
const CHAIR_SIZE = 0.45;
/** Along-edge room per seat (chair + elbow room) — drives the dynamic count. */
const SEAT_PITCH = 0.62;
/** Gap between the table edge and the chair front. */
const CHAIR_GAP = 0.04;
/** Minimum table depth before the two end (head) seats fit. */
const MIN_END_SEAT_DEPTH = 0.6;

/**
 * Push one chair symbol at parent-local (u, n), facing the table along the
 * parent-local unit direction (faceU, faceN). The chair SVG's backrest is at
 * its local -Y, so the sub-frame's `norm` points AT the table.
 */
function pushChair(out: number[], parent: SketchObjectFrame, u: number, n: number, faceU: number, faceN: number): void {
  const normX = faceU * parent.dir.x + faceN * parent.norm.x;
  const normY = faceU * parent.dir.y + faceN * parent.norm.y;
  const chairFrame: SketchObjectFrame = {
    center: at(parent, u, n),
    dir: { x: normY, y: -normX },
    norm: { x: normX, y: normY },
    width: CHAIR_SIZE,
    depth: CHAIR_SIZE,
    z: parent.z,
  };
  beginGlyphInstance(out, 'chair', chairFrame);
  transformUnitSegments(getUnitGlyph('chair', chairSvg), chairFrame, out);
  endGlyphInstance(out);
}

/** Chairs around a rectangular table: as many as fit along both long sides,
 *  plus (opt-in) one head seat per end once the table is deep enough. */
function pushRectangularChairs(out: number[], frame: SketchObjectFrame, headChairs: boolean): void {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  const chairN = hd + CHAIR_GAP + CHAIR_SIZE / 2;

  const sideCount = Math.floor(frame.width / SEAT_PITCH);
  for (let i = 0; i < sideCount; i++) {
    const u = -hw + ((i + 0.5) * frame.width) / sideCount;
    pushChair(out, frame, u, chairN, 0, -1);
    pushChair(out, frame, u, -chairN, 0, 1);
  }

  if (headChairs && frame.depth >= MIN_END_SEAT_DEPTH) {
    const chairU = hw + CHAIR_GAP + CHAIR_SIZE / 2;
    pushChair(out, frame, chairU, 0, -1, 0);
    pushChair(out, frame, -chairU, 0, 1, 0);
  }
}

/** Chairs around a round/elliptical table: as many as the perimeter seats,
 *  evenly spread, each facing the center. */
function pushRoundChairs(out: number[], frame: SketchObjectFrame): void {
  const hw = frame.width / 2;
  const hd = frame.depth / 2;
  // Ramanujan's ellipse-perimeter approximation — plenty for a seat count.
  const perimeter = Math.PI * (3 * (hw + hd) - Math.sqrt((3 * hw + hd) * (hw + 3 * hd)));
  const count = Math.floor(perimeter / SEAT_PITCH);
  for (let i = 0; i < count; i++) {
    const t = (2 * Math.PI * i) / count;
    // Outward ellipse normal at t (gradient of the implicit equation).
    const nx = Math.cos(t) / Math.max(hw, 1e-6);
    const ny = Math.sin(t) / Math.max(hd, 1e-6);
    const nLen = Math.hypot(nx, ny);
    const outU = nx / nLen;
    const outN = ny / nLen;
    const off = CHAIR_GAP + CHAIR_SIZE / 2;
    pushChair(out, frame, hw * Math.cos(t) + outU * off, hd * Math.sin(t) + outN * off, -outU, -outN);
  }
}

/**
 * Table: free-placed furniture (POINT anchor + FREE constraint — aligns to a
 * wall when placed against one, rotates with `R` otherwise). The `shape`
 * param switches between a rectangular top (with a small inner inset line)
 * and a round/elliptical top sized by the width/depth properties. The
 * `chairs` toggle (on by default) surrounds the top with chair symbols —
 * the count adapts to the shape and size: seats along both long sides on a
 * rectangular table (the two end/head seats are a separate opt-in
 * `headChairs` toggle, off by default — they tend to collide with adjacent
 * furniture), evenly around the perimeter on a round one. Resizing the
 * table reflows the seating.
 */
function buildTableGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  if (params.shape === 'round') {
    let prev: Pt2 | null = null;
    for (let i = 0; i <= ROUND_SEGMENTS; i++) {
      const t = (2 * Math.PI * i) / ROUND_SEGMENTS;
      const pt = at(frame, hw * Math.cos(t), hd * Math.sin(t));
      if (prev) pushSegment(out, prev, pt, frame.z);
      prev = pt;
    }
    if (params.chairs) pushRoundChairs(out, frame);
    return out;
  }

  // Rectangular top with a light inner inset line so it reads as a table
  // (not just a box) among other rectangles on the plan.
  pushRect(out, frame, -hw, hw, -hd, hd);
  const inset = Math.min(frame.width, frame.depth) * 0.06;
  pushRect(out, frame, -hw + inset, hw - inset, -hd + inset, hd - inset);
  if (params.chairs) pushRectangularChairs(out, frame, params.headChairs);
  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'table',
    label: t('Table'),
    category: SketchObjectCategory.FURNITURE,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 2.0, depth: 0.9 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildTableGlyph },
  }),
);
